import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "sonner";
import {
  fetchOpenCallList,
  createOpenCall,
  getOpenCallDetails,
  updateOpenCall,
  blockUnblockOpenCall,
  deleteOpenCall,
  fetchAuditionsByOpenCall,
  blockUnblockAudition,
  fetchReportedAuditions,
  deleteAudition,
  fetchAuditionReports,
} from "src/services/opencall.service";

interface FetchOpenCallListPayload {
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
}

interface CreateOpenCallPayload {
  [key: string]: any;
}

interface UpdateOpenCallPayload {
  ocid: string;
  data: any;
}

interface FetchAuditionsByOpenCallPayload {
  ocid: string;
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
  ocrId?: string;
}

interface FetchReportedAuditionsPayload {
  page: number;
  limit: number;
  search?: string;
  includeRoles?: boolean;
}

export const doFetchOpenCallList = createAsyncThunk(
  "openCall/fetchOpenCallList",
  async (payload: FetchOpenCallListPayload) => {
    try {
      const response: any = await fetchOpenCallList({
        page: payload.page,
        limit: payload.limit,
        search: payload.search,
        includeRoles: payload.includeRoles,
      });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch open call list";
      console.log(errorMessage);
      throw errorMessage;
    }
  }
);

export const doCreateOpenCall = createAsyncThunk(
  "openCall/createOpenCall",
  async (payload: CreateOpenCallPayload) => {
    try {
      const response: any = await createOpenCall(payload);
      toast.success(response.message || "Open call created successfully");
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to create open call";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);

export const doGetOpenCallDetails = createAsyncThunk(
  "openCall/getOpenCallDetails",
  async (ocid: string) => {
    try {
      const response: any = await getOpenCallDetails(ocid);
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch open call details";
      console.log(errorMessage);
      throw errorMessage;
    }
  }
);

export const doUpdateOpenCall = createAsyncThunk(
  "openCall/updateOpenCall",
  async (payload: UpdateOpenCallPayload) => {
    try {
      const response: any = await updateOpenCall(payload.ocid, payload.data);
      toast.success(response.message || "Open call updated successfully");
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to update open call";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);

export const doBlockUnblockOpenCall = createAsyncThunk(
  "openCall/blockUnblockOpenCall",
  async (ocid: string) => {
    try {
      const response: any = await blockUnblockOpenCall(ocid);
      toast.success(
        response.message || "Open call status updated successfully"
      );
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to update open call status";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);

export const doDeleteOpenCall = createAsyncThunk(
  "post/deleteOpenCall",
  async (ocid: string) => {
    try {
      const response: any = await deleteOpenCall(ocid);
      toast.success(response.message);
      return { ocid, ...response };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete open call";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);

export const doFetchAuditionsByOpenCall = createAsyncThunk(
  "openCall/fetchAuditionsByOpenCall",
  async (payload: FetchAuditionsByOpenCallPayload) => {
    try {
      const response: any = await fetchAuditionsByOpenCall({
        ocid: payload.ocid,
        page: payload.page,
        limit: payload.limit,
        search: payload.search,
        includeRoles: payload.includeRoles,
        ocrId: payload.ocrId,
      });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch auditions";
      console.log(errorMessage);
      throw errorMessage;
    }
  }
);

export const doBlockUnblockAudition = createAsyncThunk(
  "openCall/blockUnblockAudition",
  async (pid: string) => {
    try {
      const response: any = await blockUnblockAudition(pid);
      toast.success(response.message || "Audition status updated");
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to update audition status";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);

export const doFetchReportedAuditions = createAsyncThunk(
  "openCall/fetchReportedAuditions",
  async (payload: FetchReportedAuditionsPayload) => {
    try {
      const response: any = await fetchReportedAuditions({
        page: payload.page,
        limit: payload.limit,
        search: payload.search,
        includeRoles: payload.includeRoles,
      });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch reported auditions";
      console.log(errorMessage);
      throw errorMessage;
    }
  }
);

export const doFetchAuditionReports = createAsyncThunk( 
  "opencall/fetchAuditionReports",
  async (pid: string) => {
    try {
      const response: any = await fetchAuditionReports(pid);
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch audition reports";
      console.log(errorMessage);
      throw errorMessage;
    }
  }
);

export const doDeleteAudition = createAsyncThunk(
  "openCall/deleteAudition",
  async (pid: string) => {
    try {
      const response: any = await deleteAudition(pid);
      toast.success(response.message || "Audition deleted successfully");
      return { pid, ...response };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete audition";
      toast.error(errorMessage);
      throw errorMessage;
    }
  }
);
